import React from 'react'
import { ColumnSort } from '@tanstack/react-table'
import { RouteContext } from '../../../../../components/route-provider'
import { useContrastQuery } from '../../../../../hooks/use-contrast-query'
import { GetLibraryDataProps, getData } from '..'
import { useLibrariesPageContext } from '../../../pages'
import {
  LibraryUsageLicenseResponseType,
  LibraryUsageOrgProfileResponseType,
  LibraryUsageResponseType
} from './response-types'

const libraryUsageKey = 'library-usage'

export const useFetchOrgProfile = () => {
  const { organizationID } = React.useContext(RouteContext)
  const url = `/Contrast/api/ng/profile/organizations/${organizationID}?expand=freemium`

  const result = useContrastQuery<
    GetLibraryDataProps,
    LibraryUsageOrgProfileResponseType
  >([`${libraryUsageKey}-getOrgProfile`, organizationID], getData, {
    url,
    method: 'GET'
  })

  return {
    ...result,
    isOrgProfileLoading: result.isLoading,
    orgProfile: result.data
  }
}

export const useFetchLicenses = () => {
  const { organizationID } = React.useContext(RouteContext)
  const url = `/Contrast/api/ng/${organizationID}/licenses`

  const result = useContrastQuery<
    GetLibraryDataProps,
    LibraryUsageLicenseResponseType
  >([`${libraryUsageKey}-getLicenses`, organizationID], getData, {
    url,
    method: 'GET'
  })

  return {
    ...result,
    isLicensesLoading: result.isLoading,
    licenses: result.data
  }
}

export const useLibraryUsage = ({
  libraryHash,
  offset,
  limit,
  sortingOrder,
  enabled,
  searchText
}: {
  libraryHash: string
  offset: number
  limit: number
  sortingOrder: ColumnSort[]
  enabled: boolean
  searchText: string | undefined
}) => {
  const { orgProfile, isOrgProfileLoading } = useFetchOrgProfile()
  const { licenses, isLicensesLoading } = useFetchLicenses()
  const { applicationID } = useLibrariesPageContext()
  const { organizationID } = React.useContext(RouteContext)

  const url = `/Contrast/api/ng/organizations/${organizationID}/applications/${applicationID}/libraries/${libraryHash}/reports/library-usage?offset=${offset}&limit=${limit}&sortBy=${
    sortingOrder[0].id
  }&sortDirection=${sortingOrder[0].desc ? 'DESC' : 'ASC'}${
    searchText ? `&searchText=${searchText}` : ``
  }`

  const result = useContrastQuery<
    GetLibraryDataProps,
    LibraryUsageResponseType
  >(
    [`${libraryUsageKey}-getLibraryUsage`, sortingOrder, libraryHash, url],
    getData,
    {
      url,
      method: 'GET'
    },
    { enabled }
  )

  return {
    ...result,
    isLibraryUsageLoading: result.isLoading,
    libraryUsage: result.data,
    orgProfile,
    isOrgProfileLoading,
    licenses,
    isLicensesLoading
  }
}
